// ── TTS client ───────────────────────────────────────────────────────────────
// Single source of truth for which TTS backend to call.
//
// Env vars (Create React App requires REACT_APP_* prefix):
//   REACT_APP_USE_MODAL          — "true" to use Modal (streaming), anything else for SageMaker.
//   REACT_APP_TTS_URL            — SageMaker-backed Lambda endpoint.
//   REACT_APP_MODAL_URL          — Modal /synthesize_stream URL.
//                                  Returns a sequence of [4-byte BE uint32 length][WAV bytes] frames.
//   REACT_APP_MODAL_HEALTH_URL   — Modal health URL for prewarming.
//   REACT_APP_LOCAL_ENV          — "true" to enable verbose [TTS] console logging.

export const USE_MODAL = process.env.REACT_APP_USE_MODAL === 'true';

const TTS_URL = process.env.REACT_APP_TTS_URL;
const MODAL_URL = process.env.REACT_APP_MODAL_URL;
const MODAL_HEALTH_URL = process.env.REACT_APP_MODAL_HEALTH_URL;
const DEBUG = process.env.REACT_APP_LOCAL_ENV === 'true';

const log = (...args) => {
  if (DEBUG) console.log(...args);
};
const warn = (...args) => {
  if (DEBUG) console.warn(...args);
};

log('[TTS] Backend:', USE_MODAL ? 'Modal (streaming)' : 'SageMaker');
if (USE_MODAL) {
  log('[TTS] Modal URL:', MODAL_URL);
  log('[TTS] Modal Health URL:', MODAL_HEALTH_URL);
} else {
  log('[TTS] SageMaker URL:', TTS_URL);
}

// ── Prewarm ──────────────────────────────────────────────────────────────────

export const prewarmModal = async () => {
  if (!USE_MODAL) {
    log('[TTS] Prewarm skipped — using SageMaker backend');
    return;
  }
  if (!MODAL_HEALTH_URL) {
    warn('[TTS] Prewarm skipped — REACT_APP_MODAL_HEALTH_URL not set');
    return;
  }
  log('[TTS] Prewarming Modal endpoint…', MODAL_HEALTH_URL);
  try {
    const t0 = performance.now();
    const res = await fetch(MODAL_HEALTH_URL, { method: 'GET' });
    const ms = Math.round(performance.now() - t0);
    log('[TTS] Prewarm complete — status %d (%dms)', res.status, ms);
  } catch (err) {
    warn('[TTS] Prewarm request failed (non-fatal):', err.message);
  }
};

// ── Voice payload ─────────────────────────────────────────────────────────────
// The default selection. The backend has a named `youngWomanNew` voice, so it's
// sent explicitly like any other voice (previously this was treated as a
// sentinel and the `voice` field was dropped so the model used its own default).
export const DEFAULT_VOICE = 'youngWomanNew';

// Human-readable names for every voice id that can reach the UI — the backend
// model ids (`youngWomanNew`, `TonyNormal`) and the prerecorded showcase ids
// (`newslady`, `sekuru`). Keyed by the exact id so nothing user-facing ever
// shows a raw model name. `voiceLabel` falls back to the id, which would be a
// visible bug rather than a silent one.
export const VOICE_LABELS = {
  Woman: 'Woman',
  youngWomanNew: 'Young woman',
  TonyNormal: 'Man',
  Sekuru: 'Man',
  newslady: 'Woman',
  sekuru: 'Man',
};

export const voiceLabel = id => VOICE_LABELS[id] ?? id;

// ── cfg guidance knobs ───────────────────────────────────────────────────────
// Optional float knobs passed straight through to the model. They're only
// exposed in the UI to internal users, and any knob left unset is omitted from
// the payload entirely so the backend keeps its own default for it.
// Callers pass them as a `cfg` object keyed by these exact API names.
export const CFG_FIELDS = [
  'cfg_spk',
  'cfg_text',
  'cfg_spk_late',
  'cfg_t_switch',
];

// Keep only the knobs that carry a real number — everything else is dropped.
const cleanCfg = (cfg = {}) => {
  const out = {};
  for (const key of CFG_FIELDS) {
    const v = cfg?.[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = v;
  }
  return out;
};

// Stable string form of the supplied knobs, for cache keys. Empty when none
// are set, so keys match what they were before cfg existed.
export const cfgSignature = cfg => {
  const clean = cleanCfg(cfg);
  return CFG_FIELDS.filter(key => key in clean)
    .map(key => `${key}=${clean[key]}`)
    .join(',');
};

// Build the JSON request body, always including the selected `voice`.
// `historyToken` is a signed-in reader's access token: Modal uses it to save
// the finished clip to their History. A guest has none, so nothing is saved.
const ttsBody = ({ text, speed, voice, cfg, historyToken }) => {
  const payload = { text, speed };
  if (voice) payload.voice = voice;
  if (historyToken) payload.history_token = historyToken;
  return JSON.stringify({ ...payload, ...cleanCfg(cfg) });
};

// ── errors ───────────────────────────────────────────────────────────────────
// What the page says when audio can't be made, one sentence per kind of
// failure. The technical detail — status code, the server's response body, the
// browser's own wording — goes in the TTSError's message for the console and is
// never shown: a server body can carry internals, and "Failed to fetch" or
// "Load failed" means nothing to a reader.
export const ERROR_MESSAGES = {
  network:
    "Couldn't reach the voice service. Check your internet connection and try again.",
  busy: 'The voice service is busy right now. Please try again in a minute.',
  server:
    "The voice service couldn't make this audio. Please try again in a moment.",
  unsupported:
    "This browser can't play streamed audio. Please try an up-to-date Chrome, Safari or Firefox.",
  playback: "The audio couldn't be played. Please try generating it again.",
  tooLong: 'This text is too long to read aloud. The limit is 500 characters.',
  unknown: 'Something went wrong while making the audio. Please try again.',
};

export class TTSError extends Error {
  constructor(kind, detail) {
    super(detail);
    this.name = 'TTSError';
    this.kind = kind;
  }
}

// The ERROR_MESSAGES key for anything a TTS request threw. A plain Error is a
// failure nobody classified — most likely a bug — so it is 'unknown' and gets
// the generic line rather than its own message.
export const errorKind = err =>
  err instanceof TTSError && ERROR_MESSAGES[err.kind] ? err.kind : 'unknown';

// The sentence to show for anything a TTS request threw.
export const errorMessage = err => ERROR_MESSAGES[errorKind(err)];

// An abort is passed through untouched, since callers tell it apart by name
// and stay silent for it; anything else the network layer throws means the
// connection failed.
const asNetworkError = err =>
  err.name === 'AbortError' ? err : new TTSError('network', err.message);

// POSTs a synthesis request and returns the response once it is known to be
// a success.
const postTTS = async (url, { signal, ...request }) => {
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: ttsBody(request),
      signal,
    });
  } catch (err) {
    throw asNetworkError(err);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new TTSError(
      res.status === 429 ? 'busy' : 'server',
      `HTTP ${res.status}: ${body}`
    );
  }
  return res;
};

// ── SageMaker (single-blob) request ──────────────────────────────────────────

export const requestTTS = async ({ text, speed, voice, cfg, signal }) => {
  log(
    '[TTS] SageMaker request — voice: %s, speed: %s, chars: %d',
    voice ?? '(default)',
    speed,
    text.length
  );
  const t0 = performance.now();
  const res = await postTTS(TTS_URL, { text, speed, voice, cfg, signal });
  const data = await res.json();
  const src =
    data.audio_url ??
    (data.audio_base64 ? `data:audio/wav;base64,${data.audio_base64}` : null);
  if (!src) throw new TTSError('server', 'No audio in response');
  log(
    '[TTS] SageMaker response received (%dms) — src type: %s',
    Math.round(performance.now() - t0),
    data.audio_url ? 'audio_url' : 'audio_base64'
  );
  return { src };
};

// ── Modal streaming request ──────────────────────────────────────────────────
// Streams [4-byte BE length][WAV bytes] frames from /synthesize_stream.
// onChunk is awaited per frame so callers can persist before the next read.
// Returns the total chunk count.

export const streamTTS = async ({
  text,
  speed,
  voice,
  cfg,
  historyToken,
  signal,
  onChunk,
}) => {
  log(
    '[TTS] Modal stream request — voice: %s, speed: %s, chars: %d',
    voice ?? '(default)',
    speed,
    text.length
  );
  const t0 = performance.now();
  const res = await postTTS(MODAL_URL, {
    text,
    speed,
    voice,
    cfg,
    historyToken,
    signal,
  });
  if (!res.body) {
    throw new TTSError('unsupported', 'No response body to stream');
  }

  const reader = res.body.getReader();
  let buf = new Uint8Array(0);
  let idx = 0;

  const concat = (a, b) => {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
  };

  while (true) {
    let read;
    try {
      read = await reader.read();
    } catch (err) {
      // The connection dropped partway, which phones on a weak signal do.
      throw asNetworkError(err);
    }
    const { value, done } = read;
    if (done) break;
    buf = concat(buf, value);

    // Drain as many complete frames as the buffer holds.
    while (buf.length >= 4) {
      const dv = new DataView(buf.buffer, buf.byteOffset, 4);
      const n = dv.getUint32(0, false); // big-endian
      if (buf.length < 4 + n) break;
      const wav = buf.slice(4, 4 + n);
      buf = buf.slice(4 + n);
      const blob = new Blob([wav], { type: 'audio/wav' });
      log('[TTS] Stream chunk %d received (%d bytes)', idx, wav.length);
      // eslint-disable-next-line no-await-in-loop
      await onChunk(blob, idx);
      idx++;
    }
  }

  if (buf.length > 0) {
    warn(
      '[TTS] Stream ended with %d trailing bytes (incomplete frame)',
      buf.length
    );
  }
  // A 200 that carried no audio would otherwise end the request in silence,
  // with nothing on screen to say why.
  if (idx === 0) throw new TTSError('server', 'Stream contained no audio');
  log(
    '[TTS] Stream complete — %d chunks (%dms)',
    idx,
    Math.round(performance.now() - t0)
  );
  return idx;
};

// ── Cache Storage helpers ────────────────────────────────────────────────────
// Keyed by sha256(voice|speed|text). Stores one entry per chunk plus a
// manifest entry recording the total chunk count, so we can detect
// partial caches and ignore them.

const CACHE_NAME = 'tts-chunks-v1';
const cacheAvailable = () => typeof caches !== 'undefined';

const sha256Hex = async str => {
  const buf = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// The cfg knobs change the generated audio, so they take part in the key.
// When none are set the key is unchanged from before they existed, keeping
// already cached audio valid.
export const ttsCacheKey = (text, speed, voice, cfg) => {
  const sig = cfgSignature(cfg);
  return sha256Hex(`${voice}|${speed}|${text}${sig ? `|${sig}` : ''}`);
};

const chunkUrl = (hash, idx) => `https://tts-cache.local/${hash}/${idx}`;
const manifestUrl = hash => `https://tts-cache.local/${hash}/manifest`;

// Returns an array of WAV blobs in order, or null if cache miss / partial.
export const getCachedAudio = async hash => {
  if (!cacheAvailable()) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const manifest = await cache.match(manifestUrl(hash));
    if (!manifest) return null;
    const { count } = await manifest.json();
    if (!Number.isInteger(count) || count <= 0) return null;
    const blobs = [];
    for (let i = 0; i < count; i++) {
      // eslint-disable-next-line no-await-in-loop
      const res = await cache.match(chunkUrl(hash, i));
      if (!res) {
        warn(
          '[TTS] Cache partial — chunk %d missing of %d, ignoring cache',
          i,
          count
        );
        return null;
      }
      // eslint-disable-next-line no-await-in-loop
      blobs.push(await res.blob());
    }
    log('[TTS] Cache hit — %d chunks for %s', count, hash.slice(0, 8));
    return blobs;
  } catch (err) {
    warn('[TTS] Cache read failed:', err.message);
    return null;
  }
};

export const putCachedChunk = async (hash, idx, blob) => {
  if (!cacheAvailable()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(
      new Request(chunkUrl(hash, idx)),
      new Response(blob, { headers: { 'Content-Type': 'audio/wav' } })
    );
  } catch (err) {
    warn('[TTS] Cache write (chunk) failed:', err.message);
  }
};

export const putCachedManifest = async (hash, count) => {
  if (!cacheAvailable()) return;
  try {
    const cache = await caches.open(CACHE_NAME);
    await cache.put(
      new Request(manifestUrl(hash)),
      new Response(JSON.stringify({ count }), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
    log(
      '[TTS] Cache manifest stored — %s → %d chunks',
      hash.slice(0, 8),
      count
    );

    // Keep only the most recent entry — evict everything else.
    const keys = await cache.keys();
    await Promise.all(
      keys
        .filter(req => !req.url.includes(`/${hash}/`))
        .map(req => cache.delete(req))
    );
    if (keys.length > count + 1) {
      log('[TTS] Cache evicted %d stale entries', keys.length - (count + 1));
    }
  } catch (err) {
    warn('[TTS] Cache write (manifest) failed:', err.message);
  }
};

// Debug helper — call from console to wipe all cached TTS audio.
export const clearTTSCache = async () => {
  if (!cacheAvailable()) return false;
  const ok = await caches.delete(CACHE_NAME);
  log('[TTS] Cache cleared:', ok);
  return ok;
};

if (typeof window !== 'undefined' && DEBUG) {
  window.__clearTTSCache = clearTTSCache;
}
